import { Typography } from '@mui/material';
import React, { FC, useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { OrganizationRole } from '../../../api/api';
import { useAuth } from '../../../auth/auth-provider';
import { localized } from '../../../i18n/i18n';
import { selectOrganization } from '../../../state/features/settings/settings-slice';
import { selectUsers } from '../../../state/features/users/users-slice';
import { AppState } from '../../../state/store';
import { AddNewButton } from '../../components/specific-components/buttons/add-new-button';
import { useRightSidebar } from '../../components/specific-components/right-side-bar';
import { HeadCell, TableList } from '../../components/specific-components/table-list/table-list';
import { UserStandardTableRow } from '../../components/specific-components/table-list/users/user-standard-table-row';
import { EditUserProfile } from './side-content/edit-user-profile';
import { userTableMaxHeight } from './users';
import { AddAndManageUsersStepTargets } from '../../../utilities/enums/tutorial-steps-target';

const headCells: HeadCell[] = [
  {
    id: 'name',
    label: localized('Name'),
    align: 'left',
  },
  {
    id: 'email',
    label: localized('Email'),
    align: 'left',
  },
  {
    id: 'phoneNr',
    label: localized('PhoneNr'),
    align: 'left',
  },
  {
    id: 'serviceArea',
    label: localized('ServiceArea'),
    align: 'left',
  },
  {
    id: 'status',
    label: localized('Status'),
    align: 'left',
  },
  {
    id: 'edit',
    label: localized('Edit'),
    align: 'center',
    width: '20px',
  },
  {
    id: 'delete',
    label: localized('Delete'),
    align: 'center',
    width: '20px',
  },
];

export const UsersManagers: FC = React.memo(() => {
  const setSidebarContent = useRightSidebar();
  const users = useSelector(selectUsers);
  const selectedOrganization = useSelector(selectOrganization);
  const { isSuccess } = useSelector((store: AppState) => store.usersReducer);
  const showSpinner = useSelector(
    (store: AppState) => store.usersReducer.pending && store.usersReducer.users.length < 1,
  );
  const { isAdmin } = useAuth();

  // Clear sidebar when swapping page
  useEffect(() => {
    return () => setSidebarContent(null);
  }, [setSidebarContent]);

  useEffect(() => {
    if (isSuccess) setSidebarContent(null);
  }, [isSuccess, setSidebarContent]);

  const addManager = useCallback(() => {
    setSidebarContent(<EditUserProfile role={OrganizationRole.Manager} />);
  }, [setSidebarContent]);

  const filteredUsers = users.filter((user) => {
    if (selectedOrganization) {
      return (
        user.organizationUser?.find((oU) => oU.organizationId === selectedOrganization.id)?.organizationRole ===
        OrganizationRole.Manager
      );
    }
  });

  return (
    <>
      <Typography fontSize={'30px'} fontWeight={'bold'}>
        {localized('AllManagers')}
      </Typography>
      <Typography variant="body1" whiteSpace={'pre-line'} marginBottom={1}>
        {localized('MetaTextManagers')}
      </Typography>
      {isAdmin && (
        <AddNewButton onClick={addManager} id={AddAndManageUsersStepTargets.Step3}>
          {localized('AddManager')}
        </AddNewButton>
      )}
      <TableList headCells={headCells} showSpinner={showSpinner} maxHeight={userTableMaxHeight}>
        {filteredUsers.map((user, i) => (
          <UserStandardTableRow key={i} user={user} id={i === 0 ? AddAndManageUsersStepTargets.Step9 : undefined} />
        ))}
      </TableList>
    </>
  );
});
